<script setup lang="ts">
import { computed, ref } from 'vue'

import { Button, PopoverMenu, PopoverMenuOption } from '@collector/shared-ui'
import { Incident, OccursAction } from '@collector/sportsapi-types'
import { useEventIncidentsQueue } from '@desktop/views/Relation/Sports/composables'
import {
  getIncidentsGroupRepresentativeIncident,
  IncidentsGroup,
  isIncidentsGroup,
} from '@desktop/views/Relation/Sports/configuration'

interface Props {
  incident: Incident
  nextIncident: Incident | IncidentsGroup | undefined
  previousIncident: Incident | IncidentsGroup | undefined
}

const props = defineProps<Props>()

// composables
const eventIncidentsQueue = useEventIncidentsQueue()

// state
const isPopoverMenuOpened = ref(false)
const options = computed<PopoverMenuOption<OccursAction>[]>(() => [
  {
    name: 'Move up',
    value: 'later',
    disabled: isMoveDisabled(props.previousIncident),
  },
  {
    name: 'Move down',
    value: 'prior',
    disabled: isMoveDisabled(props.nextIncident),
  },
])

// methods
function getTime(
  incident: Incident | IncidentsGroup,
): [number | null, number | null] {
  const i = isIncidentsGroup(incident)
    ? getIncidentsGroupRepresentativeIncident(incident)
    : incident
  return [i.minute, i.second]
}

function isMoveDisabled(
  adjacent: Incident | IncidentsGroup | undefined,
): boolean {
  if (!adjacent || isIncidentsGroup(adjacent)) return true

  const [m1, s1] = getTime(props.incident)
  const [m2, s2] = getTime(adjacent)
  if ([m1, s1, m2, s2].includes(null)) return false
  return !(m1 === m2 && s1 === s2)
}

function moveIncident(action: OccursAction): void {
  isPopoverMenuOpened.value = false

  const incident = props.incident

  if (incident && !isIncidentsGroup(incident)) {
    eventIncidentsQueue.changeIncidentOrder(incident, action)
  }
}
</script>

<template>
  <PopoverMenu
    v-model="isPopoverMenuOpened"
    :options
    :content="{
      align: 'end',
    }"
    @clickOption="(action) => moveIncident(action)"
  >
    <template #trigger>
      <Button
        class="bg-neutral-light-7 border-neutral-light-0 hover:enabled:bg-neutral-3 hover:enabled:border-neutral-3 font-bold"
        icon="sort"
        iconSize="sm"
        square
        size="xs"
      />
    </template>
  </PopoverMenu>
</template>
